'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useOptimistic, useTransition } from 'react'

import {
  LOCK_AB_AF_SUB2,
  LOCK_AB_EXPOSURE_CODE,
  LOCK_AB_EXPOSURE_LABEL,
  withLockAbAfSubs,
} from '@/app/(makers)/modoocar/_features/modoocar-lock-ab'
import { useRankLockAb } from '@/app/(makers)/modoocar/_features/modoocar-lock-ab/_hooks/useLockAb'
import {
  getCurrentPagePath,
  useSeoOnelinkUrl,
  withWebshowAfDp,
} from '@/shared/external/onelink'

import {
  buildRankPath,
  getRankTabDescription,
  getRankTabLabel,
  getRankTabTitle,
  RANK_CTA_ONELINK_URL,
  RANK_DEFAULT_SEGMENT,
  RANK_EVENT,
  RANK_SEGMENTS,
  RANK_TABS,
  RANK_TRACK_CODE,
  RANK_TYPE_LABEL,
  RANK_VISIBLE_LIMIT,
  resolveRankFuelType,
} from '../_constants'
import { checkIsInApp, useIsInApp } from './useIsInApp'
import { useRankBottomCta } from './useRankBottomCta'
import { useRankTracking } from './useRankTracking'

import type { IRankingList, TRankFuelType, TRankTab } from '../_types'

const HERO_CAR_COUNT = 3
/** [주의]: 탭·차급·유종을 바꾸면 라우트가 바뀌어 화면이 다시 마운트됩니다. page_show와 A/B 노출을 진입당 한 번만 보내려고 모듈 레벨 플래그를 씁니다. */
let hasTrackedPageShow = false
let hasTrackedLockAbExposure = false
const LEGACY_RANK_QUERY_KEYS = ['tab', 'segment', 'fuelType']

interface IUseRankingPageParams {
  ranking: IRankingList
  activeTab: TRankTab
  activeSegment: string
  fuelType: TRankFuelType
}

export const useRankingPage = ({
  ranking,
  activeTab,
  activeSegment,
  fuelType,
}: IUseRankingPageParams) => {
  const router = useRouter()
  const isInApp = useIsInApp()
  const [, startTransition] = useTransition()

  const [selection, setSelection] = useOptimistic(
    { tab: activeTab, segment: activeSegment, fuelType },
    (
      _prev,
      next: { tab: TRankTab; segment: string; fuelType: TRankFuelType }
    ) => next
  )
  const {
    ref: bottomCtaRef,
    visible: bottomCtaVisible,
    height: bottomCtaHeight,
  } = useRankBottomCta()

  const commonParams = useMemo(
    () => ({
      rank_type: RANK_TYPE_LABEL[selection.tab],
      category: selection.segment,
      mode: selection.fuelType,
    }),
    [selection.tab, selection.segment, selection.fuelType]
  )
  const rankTrack = useRankTracking(commonParams)

  useEffect(
    function trackPageShowOnce() {
      if (hasTrackedPageShow) return
      hasTrackedPageShow = true
      rankTrack.pageShow()
    },
    [rankTrack]
  )

  const lockAb = useRankLockAb(checkIsInApp)
  useEffect(
    function trackLockAbExposureOnce() {
      if (hasTrackedLockAbExposure || !lockAb.from) return
      hasTrackedLockAbExposure = true
      rankTrack.track(RANK_EVENT.SECTION_SHOW, {
        code: LOCK_AB_EXPOSURE_CODE,
        label: LOCK_AB_EXPOSURE_LABEL,
        status: lockAb.variant,
        from: lockAb.from,
      })
    },
    [lockAb, rankTrack]
  )

  const navigate = (
    next: {
      tab?: TRankTab
      segment?: string
      fuelType?: string
    },
    options?: { scrollToTop?: boolean; withSkeleton?: boolean }
  ) => {
    const nextTab = next.tab ?? selection.tab
    const nextSegment =
      nextTab === 'fuelCost'
        ? RANK_DEFAULT_SEGMENT
        : (next.segment ?? selection.segment)
    // [정책]: 차급을 바꿔도 유종은 이어 가지만 전기는 넘기지 않습니다.
    const carriedFuelType =
      selection.fuelType === '전기' ? undefined : selection.fuelType
    const nextFuelType = resolveRankFuelType(
      next.fuelType ?? carriedFuelType,
      nextSegment
    )
    const path = buildRankPath({
      tab: nextTab,
      segment: nextSegment,
      fuelType: nextFuelType,
    })
    // [정책]: webview·utm·seoOnelink 같은 부가 쿼리는 이동할 때도 남겨서 어트리뷰션과 인앱 미리보기를 유지합니다.
    const preserved = new URLSearchParams(window.location.search)
    LEGACY_RANK_QUERY_KEYS.forEach(key => preserved.delete(key))
    const query = preserved.toString()
    const url = query ? `${path}?${query}` : path
    if (options?.scrollToTop && typeof window !== 'undefined') {
      window.scrollTo({ top: 0 })
    }

    if (options?.withSkeleton) {
      router.replace(url, { scroll: false })
      return
    }
    startTransition(() => {
      setSelection({
        tab: nextTab,
        segment: nextSegment,
        fuelType: nextFuelType,
      })
      router.replace(url, { scroll: false })
    })
  }

  const mobileOnelinkUrl = useSeoOnelinkUrl(RANK_CTA_ONELINK_URL)
  // [앱]: 랭킹 URL은 동적이라 webshow로 현재 경로를 앱 웹뷰에 실어 보냅니다.
  const withRankMoreAfSubs = (url: string) =>
    withLockAbAfSubs(url, {
      afSub1: lockAb.afSub1,
      afSub2: LOCK_AB_AF_SUB2.RANK_MORE,
    })
  const goToAppMobile = () => {
    window.location.href = withRankMoreAfSubs(
      withWebshowAfDp(mobileOnelinkUrl, getCurrentPagePath())
    )
  }
  const goToAppDesktop = () => {
    window.location.href = withRankMoreAfSubs(
      withWebshowAfDp(RANK_CTA_ONELINK_URL, getCurrentPagePath())
    )
  }

  const isElectric = selection.fuelType === '전기'
  const isFuelCost = selection.tab === 'fuelCost'

  const handleTabSelect = (tab: TRankTab) => {
    const tabDef = RANK_TABS.find(item => item.id === tab)
    const tabLabel = tabDef ? getRankTabLabel(tabDef, isElectric) : tab
    rankTrack.itemClick(RANK_TRACK_CODE.TAB, 'tab', `${tabLabel} 클릭`)
    navigate({ tab }, { scrollToTop: true, withSkeleton: true })
  }
  const handleSegmentSelect = (segment: string) => {
    rankTrack.itemClick(RANK_TRACK_CODE.CATEGORY, 'filter', `${segment} 클릭`)
    navigate({ segment })
  }
  const handleFuelChange = (fuelType: string) => {
    rankTrack.itemChange(
      RANK_TRACK_CODE.SUMMARY,
      'chip',
      `${fuelType}으로 변경`
    )
    navigate({ fuelType })
  }
  const handleFuelTriggerClick = () =>
    rankTrack.itemClick(RANK_TRACK_CODE.SUMMARY, 'chip', '유종 필터 클릭')
  const handleEmptyAction = () => navigate({ segment: RANK_SEGMENTS[0].id })

  const title = getRankTabTitle(selection.tab, isElectric)
  const description = getRankTabDescription(
    selection.tab,
    isElectric,
    ranking.annualMileage
  )
  const heroImageUrls = ranking.items
    .slice(0, HERO_CAR_COUNT)
    .map(item => item.imageUrls[0])
    .filter(Boolean)
  const segmentOptions = RANK_SEGMENTS.map(segment => segment.id)
  const shareTitle = `${selection.segment} ${title}`
  const shareImageUrl = heroImageUrls[0] ?? ''
  const isEmpty = ranking.items.length === 0
  const hasMore = ranking.totalCount > RANK_VISIBLE_LIMIT

  return {
    isInApp,
    selection,
    isElectric,
    isFuelCost,
    rankTrack,
    bottomCtaRef,
    bottomCtaVisible,
    bottomCtaHeight,
    title,
    description,
    heroImageUrls,
    segmentOptions,
    shareTitle,
    shareImageUrl,
    isEmpty,
    hasMore,
    goToAppMobile,
    goToAppDesktop,
    lockAbAfSub1: lockAb.afSub1,
    handleTabSelect,
    handleSegmentSelect,
    handleFuelChange,
    handleFuelTriggerClick,
    handleEmptyAction,
  }
}
